import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ListFilters from '@/common/components/ListFilters'
import Skeleton from '@/common/components/Skeleton'
import Spinner from '@/common/components/Spinner'
import { Pagination } from '@/common/components/Table/pagination'
import TextField from '@/common/components/TextField'
import { useCities } from '@/common/hooks/queries/useCities'
import { useGeoStates } from '@/common/hooks/queries/useGeoStates'
import useDebounce from '@/common/hooks/useDebounce'
import { GeoTransformers } from '@/common/services/geoTransformers'
import { Mapper } from '@/common/services/mapper'
import { StateData } from '@/common/types/address'
import { Option } from '@/common/types/option'
import { cn } from '@/common/utils/cn'
import EstablishmentChoiceListItem from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/components/directivesFormSteps/EstablishmentChoiceEntryFormStep/EstablishmentChoiceListItem'
import EstablishmentChoiceListSkeleton from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/components/directivesFormSteps/EstablishmentChoiceEntryFormStep/EstablishmentChoiceListSkeleton'
import { useMaintenanceWorkflowAllowedEstablishments } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/hooks/useMaintenanceWorkflowAllowedEstablishments'
import { MaintenanceWorkflowAllowedEstablishmentData } from '@/features/maintenanceWorkflow/pages/Show/types/maintenanceWorkflowAllowedEstablishment.type'
import { SearchIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

interface EstablishmentChoiceListProps {
  selectedEstablishmentsIds: string[]
  onSelectEstablishment: (establishment: MaintenanceWorkflowAllowedEstablishmentData) => void
}

const EstablishmentChoiceList = ({
  selectedEstablishmentsIds,
  onSelectEstablishment,
}: EstablishmentChoiceListProps) => {
  const { id: workflowId } = useParams()

  const scrollListRef = useRef<HTMLDivElement>(null)

  const [searchEstablishment, setSearchEstablishment] = useDebounce('')
  const [searchState, setSearchState] = useDebounce('')
  const [searchCity, setSearchCity] = useDebounce('')

  const [filters, setFilters] = useState({
    state: undefined as Option | undefined,
    city: undefined as Option | undefined,
    page: 1 as number | undefined,
  })

  const { data: stateOptions } = useGeoStates({
    filters: {
      search: searchState,
    },
    mapper: Mapper.mapToOptions<StateData>({
      valueFieldName: 'id',
      labelFieldName: 'display_name',
    }),
  })

  const { data: cityOptions } = useCities({
    filters: {
      search: searchCity,
      stateId: filters.state?.value,
    },
    mapper: Mapper.mapWithTransformer({
      transformer: GeoTransformers.optionsTransformer(),
    }),
  })

  const {
    data: establishments,
    meta: establishmentsMeta,
    error: establishmentsError = {} as any,
    refetch: refetchEstablishments,
    isPending: establishmentsIsPending,
    isRefetching: establishmentsIsRefetching,
  } = useMaintenanceWorkflowAllowedEstablishments<MaintenanceWorkflowAllowedEstablishmentData[]>({
    workflowId,
    filters: {
      search: searchEstablishment,
      page: filters.page,
    },
  })

  useEffect(() => {
    scrollListRef.current?.scrollTo({ top: 0 })
  }, [filters.page])

  useEffect(() => {
    setFilters({ ...filters, page: 1 })
  }, [searchEstablishment])

  return (
    <div className="-mx-4 p-4 bg-white-100 flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <TextField
          LeftIcon={<SearchIcon />}
          placeholder="Pesquisar"
          onChange={(e) => setSearchEstablishment(e.currentTarget.value)}
        />

        <div className="flex items-center justify-between">
          <ListFilters
            filters={[
              {
                className: 'shadow-sm',
                placeholder: 'Estado',
                options: [{ label: 'Todas', value: '' }, ...stateOptions],
                value: filters.state,
                onChange: (e: any) =>
                  setFilters((prev) => ({ ...prev, state: e?.value ? e : undefined, city: undefined, page: 1 })),
                onInputChange: setSearchState,
              },
              {
                className: 'shadow-sm',
                placeholder: 'Cidade',
                options: [{ label: 'Todas', value: '' }, ...cityOptions],
                value: filters.city,
                onChange: (e: any) => setFilters((prev) => ({ ...prev, city: e?.value ? e : undefined, page: 1 })),
                onInputChange: setSearchCity,
              },
            ]}
          />

          {establishmentsIsRefetching && (
            <div className="flex items-center gap-2">
              <Spinner className="size-4 fill-white-800" />

              <span className="italic text-xs">Carregando</span>
            </div>
          )}
        </div>
      </div>

      <ErrorMessageWrapper
        className="h-[320px]"
        statusCode={establishmentsError?.status}
        refetchFunction={refetchEstablishments}
      >
        <div>
          <div ref={scrollListRef} className="flex flex-col gap-2 overflow-auto max-h-[320px] thin-scroll">
            {establishmentsIsPending &&
              new Array(2).fill(null).map((_, i) => <EstablishmentChoiceListSkeleton key={i} />)}

            {!establishmentsIsPending &&
              establishments?.map((establishment) => (
                <EstablishmentChoiceListItem
                  key={establishment.id}
                  establishment={establishment}
                  isChecked={selectedEstablishmentsIds.includes(establishment.establishment_id)}
                  onSelectEstablishment={onSelectEstablishment}
                />
              ))}
          </div>

          {!establishmentsIsPending ? (
            <div
              className={cn(
                'w-full px-4 py-2 bg-white-200 rounded-b-lg flex justify-between items-center',
                typeof establishmentsMeta.total === 'undefined' && 'justify-end',
              )}
            >
              {typeof establishmentsMeta.total !== 'undefined' ? (
                <p className="text-xs italic font-light">
                  Mostrando {establishments?.length} resultados de {establishmentsMeta.total}
                </p>
              ) : null}

              <Pagination
                currentPage={filters.page || 1}
                totalPages={establishmentsMeta.total_pages}
                setPage={(page) => setFilters({ ...filters, page: (page as number) || 1 })}
              />
            </div>
          ) : (
            <div className="h-8 w-full px-4 bg-white-200 rounded-b-lg flex justify-between items-center">
              <Skeleton className="h-4 w-40" />
            </div>
          )}
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default EstablishmentChoiceList
